<template>
  <div class="asset-viewer" v-if="activePayload">
    <div class="asset-viewer__bg touch" @click="close"></div>

    <transition name="asset-viewer-content">
      <div class="asset-viewer__content">
        <div class="asset-viewer__header">
          <div class="asset-viewer__close touch" @click="close">
            <fa :icon="['fal', 'times']" />
          </div>
          <div v-if="tagLabel" class="asset-viewer__label" v-html="tagLabel"></div>
        </div>

        <div class="asset-viewer__wrapper touch" :class="{ 'asset-viewer__wrapper--expand': expanded }">
          <div v-if="hasMultiplePayloads" class="asset-viewer__index">{{ payloadIndex + 1 }} / {{ payloadX.length }}</div>

          <div v-if="tag && tag.icon" class="asset-viewer__icon" :style="iconStyles">
            <fa :icon="['fas', tag.icon]" fixed-width />
          </div>

          <div v-if="hasMultiplePayloads" class="asset-viewer__left touch" @click="indexDown">
            <fa :icon="['fal', 'chevron-left']" />
          </div>

          <div v-if="hasMultiplePayloads" class="asset-viewer__right touch" @click="indexUp">
            <fa :icon="['fal', 'chevron-right']" />
          </div>

          <div v-if="payloadX.length > 1" class="asset-viewer__dots">
            <button v-for="(payload, index) in payloadX" :key="`asset-dot-${index}`" class="asset-viewer__dot touch" :class="{ 'asset-viewer__dot--active': payloadIndex === index }" @click.stop="payloadIndex = index"></button>
          </div>

          <transition :name="slideTransition" mode="out-in">
            <div :key="activePayload" class="asset touch" @click="openVirtualTour" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd">
              <template v-if="payloadType === 'video'">
                <div v-if="status === 'stopped' || status === 'paused'" class="pause-icon touch" @click.stop="play">
                  <fa :icon="['fas', 'play']" />
                </div>

                <div v-if="status === 'playing'" class="pause-icon touch" @click.stop="pause">
                  <fa :icon="['fal', 'pause']" />
                </div>

                <video :key="activePayload" :src="activePayload" autoplay loop playsinline disablePictureInPicture poster="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==" ref="video" :style="style" @play="onPlay" @timeupdate="progress" />

                <div v-if="playbackProgress > 0" class="asset-viewer__progress" :class="{ 'asset-viewer__progress--playing': status === 'playing' }">
                  <div class="progress-bar" :style="`width: ${playbackProgress}%`" />
                </div>
              </template>

              <template v-else-if="payloadType === 'image'">
                <img :src="activePayload" alt="" :style="style" />
              </template>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const CONTENT_STAGGER_MS = 300;
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];
const VIDEO_EXTENSIONS = ["mov", "mp4", "webm"];

export default {
  props: {
    payload: {
      type: Array,
      default: () => [],
    },
    tag: {
      type: Object,
      default: null,
    },
    virtualTourUrl: {
      type: String,
      default: "",
    },
    meta: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      expanded: true,
      isContentVisible: false,
      payloadIndex: 0,
      playbackProgress: 0,
      slideTransition: "fast-fade",
      status: "stopped",
      contentTimer: null,
      galleryNavHandler: null,

      touchStartX: 0,
      touchStartY: 0,
      touchDeltaX: 0,
      touchDeltaY: 0,
      swipeThreshold: 50,
      swipeVerticalTolerance: 60,
      didSwipe: false,
    };
  },

  watch: {
    $route() {
      this.close();
    },

    payload() {
      this.playbackProgress = 0;
    },

    payloadX(nextPayloads) {
      if (!nextPayloads.length) {
        this.payloadIndex = 0;
        return;
      }

      if (this.payloadIndex > nextPayloads.length - 1) {
        this.payloadIndex = nextPayloads.length - 1;
      }
    },

    payloadIndex() {
      if (this.$store.state.navMaster) {
        this.$store.state.socket.emit("galleryNav", this.payloadIndex);
      }
    },
  },

  beforeDestroy() {
    clearTimeout(this.contentTimer);
  },

  computed: {
    ...mapGetters({
      activeVirtualTourUrl: "property/activeVirtualTourUrl",
      language: "language",
    }),

    activePayload() {
      return this.payloadX[this.payloadIndex] || null;
    },

    hasMultiplePayloads() {
      return this.payloadX.length > 1;
    },

    tagLabel() {
      if (!this.tag || !this.tag.label) return "";
      return this.tag.label[this.language.code] || this.tag.label.en || "";
    },

    iconStyles() {
      let colorStyles = {};
      if (this.tag && this.tag.backgroundColour && this.tag.backgroundColour !== "#ffffff") {
        colorStyles = {
          backgroundColor: this.tag.backgroundColour,
        };
      }
      return Object.assign({}, colorStyles);
    },

    payloadType() {
      if (!this.activePayload) return null;
      const ext = this.getFileExtension(this.activePayload);

      if (IMAGE_EXTENSIONS.includes(ext)) {
        return "image";
      }

      if (VIDEO_EXTENSIONS.includes(ext)) {
        return "video";
      }

      return null;
    },

    payloadX() {
      let payload = [];

      if (Array.isArray(this.payload)) {
        payload = this.payload.map((url) => this.$store.state.assetsPath + url);
      }

      if (this.virtualTourUrl && this.$store.state.isMatterportAvailable) {
        payload.push("/assets/virtual-tour-slide.jpg");
      }
      return payload;
    },

    style() {
      if (!this.meta) return {};

      return {
        objectFit: this.meta.objectFit,
      };
    },
  },

  methods: {
    close() {
      clearTimeout(this.contentTimer);
      this.isContentVisible = false;
      this.$store.dispatch("actions/TRIGGER_SHOW_ASSET", "");
    },

    handleTouchStart(event) {
      if (!this.hasMultiplePayloads) return;

      const touch = event.changedTouches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.didSwipe = false;
    },

    handleTouchMove(event) {
      if (!this.hasMultiplePayloads) return;

      const touch = event.changedTouches[0];
      this.touchDeltaX = touch.clientX - this.touchStartX;
      this.touchDeltaY = touch.clientY - this.touchStartY;
    },

    handleTouchEnd() {
      if (!this.hasMultiplePayloads) return;

      const absX = Math.abs(this.touchDeltaX);
      const absY = Math.abs(this.touchDeltaY);

      const isHorizontalSwipe = absX > this.swipeThreshold && absY < this.swipeVerticalTolerance;

      if (!isHorizontalSwipe) {
        this.resetTouchState();
        return;
      }

      this.didSwipe = true;

      if (this.touchDeltaX < 0) {
        this.indexUp(); // swipe left -> next image
      } else {
        this.indexDown(); // swipe right -> previous image
      }

      this.resetTouchState();
    },

    resetTouchState() {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
    },

    play() {
      const video = this.getVideoRef();
      if (!video) return;

      video
        .play()
        .then(() => {
          this.status = "playing";
        })
        .catch(() => {
          this.status = "paused";
        });
    },

    pause() {
      const video = this.getVideoRef();
      if (!video) return;
      video.pause();
      this.status = "paused";
    },

    stop() {
      const video = this.getVideoRef();
      if (!video) return;
      video.pause();
      video.currentTime = 0;
      this.status = "stopped";
    },

    onPlay() {
      this.status = "playing";
    },

    progress() {
      const video = this.getVideoRef();

      if (video && !isNaN(video.duration)) {
        this.playbackProgress = (video.currentTime / video.duration) * 100;
      }
    },

    indexDown() {
      this.slideTransition = "fade-slide-left";

      if (this.payloadType === "video") {
        this.stop();
      }

      if (this.payloadIndex === 0) {
        this.payloadIndex = this.payloadX.length - 1;
      } else {
        this.payloadIndex--;
      }
    },

    indexUp() {
      this.slideTransition = "fade-slide-right";

      if (this.payloadType === "video") {
        this.stop();
      }

      if (this.payloadIndex === this.payloadX.length - 1) {
        this.payloadIndex = 0;
      } else {
        this.payloadIndex++;
      }
    },

    openVirtualTour() {
      if (this.didSwipe) {
        this.didSwipe = false;
        return;
      }
      if (this.virtualTourUrl && this.$store.state.isMatterportAvailable && this.payloadIndex === this.payloadX.length - 1) {
        this.$store.commit("property/SET_ACTIVE_VIRTUAL_TOUR_URL", this.virtualTourUrl);
      }
    },

    getVideoRef() {
      if (!this.$refs.video) return null;
      return Array.isArray(this.$refs.video) ? this.$refs.video[0] : this.$refs.video;
    },

    getFileExtension(path) {
      const cleanPath = path.split("#")[0].split("?")[0];
      const parts = cleanPath.split(".");
      if (parts.length <= 1) return "";
      return parts.pop().toLowerCase();
    },
  },
};
</script>

<style lang="scss" scoped>
.asset-viewer-content-enter-active {
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.asset-viewer-content-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.asset-viewer-content-enter,
.asset-viewer-content-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

.asset-viewer {
  bottom: 0;
  left: 0;
  position: absolute;
  top: 0;
  width: 160rem;
  z-index: var(--modal-z-index);

  .nav-hidden & {
    width: 100%;
  }

  &__bg {
    background: rgba(#000, 0.8);
    bottom: 0;
    left: 0;
    position: absolute;
    top: 0;
    width: 160rem;
    z-index: -1;
  }

  &__content {
    bottom: 0;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }

  &__icon {
    align-items: center;
    background-color: var(--brand-color);
    border: 0.35em solid #fff;
    border-radius: 50%;
    box-shadow: rgba(#000, 0.6) 0.1rem 0.2rem 0.4rem;
    color: var(--brand-text-color);
    display: flex;
    font-size: 0.75em;
    height: 5rem;
    justify-content: center;
    left: 50%;
    position: absolute;
    padding: 1rem;
    top: -2rem;
    transform: translateX(-50%);
    width: 5rem;
    z-index: 10;

    .nav-hidden & {
      display: none;
    }

    svg {
      height: 100%;
      width: 100%;
    }
  }

  &__dots {
    align-items: center;
    bottom: 4.5rem;
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    z-index: 10;

    .nav-hidden & {
      display: none;
    }
  }

  &__dot {
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    border: 0.1rem solid rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    box-sizing: border-box;
    display: block;
    flex: 0 0 1rem;
    height: 1rem;
    width: 1rem;
    min-width: 1rem;
    max-width: 1rem;
    min-height: 1rem;
    max-height: 1rem;
    margin: 0;
    padding: 0;
    line-height: 0;

    &--active {
      background: #fff;
      border-color: #fff;
    }
  }

  &__header {
    position: absolute;
    left: 50%;
    bottom: 23.5rem;
    transform: translateX(-50%);
    width: 100%;
    pointer-events: auto;
    z-index: 10;

    .nav-hidden & {
      bottom: 2rem;
    }
  }

  &__label {
    position: absolute;
    top: calc(100% + 1.6rem); // positioning of close button + gap
    left: 50%;
    transform: translateX(-50%);
    max-width: 24rem;
    font-size: 1.4rem;
    line-height: 1.2;
    text-align: center;
    text-shadow: 0 0 0.5rem #000;
    text-transform: uppercase;
  }

  &__index {
    bottom: 1.25rem;
    left: 1.5rem;
    position: absolute;
    z-index: 10;
  }

  &__close {
    position: relative; // relative to header
    margin: 0 auto; // horizontally center in the header
    background: var(--button-background-color);
    color: var(--button-color);
    border-radius: 50%;
    border: 0.35rem solid #fff;
    box-shadow: var(--button-box-shadow);
    height: var(--button-height);
    width: var(--button-height);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
  }

  &__expand {
    align-items: center;
    background: #cedace;
    border-radius: 50%;
    bottom: 2rem;
    color: #000;
    display: flex;
    filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, 0.6));
    height: 3rem;
    justify-content: center;
    position: absolute;
    right: 0.5rem;
    transform: translateX(-50%);
    width: 3rem;
    z-index: 10;

    .nav-hidden & {
      display: none;
    }

    svg {
      font-size: 2em;
    }
  }

  &__wrapper {
    aspect-ratio: 16/12;
    bottom: 25rem;
    left: 50%;
    pointer-events: auto;
    position: absolute;
    transform: translateX(-50%);
    transition: height 300ms ease-in-out, width 300ms ease-in-out;
    width: 100rem;

    &--expand {
      height: 73.1rem;
      width: 130rem;
    }

    .nav-hidden & {
      bottom: 0;
      left: 0;
      aspect-ratio: unset;
      height: 100%;
      width: 100%;
      transform: none;
    }

    .play-icon {
      font-size: 5rem;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;

      .nav-hidden & {
        display: none;
      }
    }

    .pause-icon {
      bottom: 0.5rem;
      left: 1rem;
      position: absolute;
      z-index: 10;

      .nav-hidden & {
        display: none;
      }

      svg {
        filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, 0.6));
        height: 4rem;
        padding: 1rem;
        width: 4rem;
      }
    }

    img,
    video {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }

  &__progress {
    bottom: 0;
    height: 0.5rem;
    left: 0;
    position: absolute;
    right: 0;

    &--playing {
      .progress-bar {
        transition: width 0.5s linear;
      }
    }

    .progress-bar {
      background: #eaeaea;
      height: 100%;
      opacity: 0.75;
      position: relative;
      width: 0;
    }
  }

  &__left {
    align-items: center;
    background: #cedace;
    border-radius: 0 0.5rem 0.5rem 0;
    bottom: 10rem;
    color: #000;
    display: flex;
    filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, 0.6));
    height: 6rem;
    justify-content: center;
    left: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 3rem;
    z-index: 10;

    svg {
      font-size: 2.8em;
    }
  }

  &__right {
    align-items: center;
    background: #cedace;
    border-radius: 0.5rem 0 0 0.5rem;
    bottom: 10rem;
    color: #000;
    display: flex;
    filter: drop-shadow(0.1rem 0.2rem 0.4rem rgba(0, 0, 0, 0.6));
    height: 6rem;
    justify-content: center;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3rem;
    z-index: 10;

    svg {
      font-size: 2.8em;
    }
  }
}

.asset {
  background-color: #000;
  bottom: 0;
  border-radius: 0.5rem;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  overflow: hidden;
}
</style>
